/**
 * POST /order/offlineSale success body (axios interceptor already unwraps once).
 * Single: flat merged order + event + QR fields + viewTicketButton.
 * Bundle (N>1): offlineTicketBundle, ticketCount, tickets[], viewTicketButton.
 *
 * Entry QR priority per unit: passQrcodes[] → qrcodeimages[] → single qrcodeimage.
 */

export function extractOfflineSaleData(res) {
  if (res == null || typeof res !== "object") return null;
  if (res.data != null && typeof res.data === "object") return res.data;
  return res;
}

/** Image URL or data URI for <img src>. */
export function offlineSaleQrImgSrc(qrcode) {
  if (!qrcode || typeof qrcode !== "string") return "";
  const s = qrcode.trim();
  if (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("data:")
  ) {
    return s;
  }
  return `data:image/jpeg;base64,${s}`;
}

function pickQrStringFromValue(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "object") {
    return String(
      v.qrcodeimage ??
        v.qrCodeImage ??
        v.qrcode ??
        v.qrCode ??
        v.image ??
        v.url ??
        ""
    ).trim();
  }
  return String(v).trim();
}

/**
 * Collect entry QRs for one sold ticket unit (or view-tickets row).
 * Priority: passQrcodes[] → qrcodeimages[] → single qrcodeimage / qrcode.
 */
export function collectTicketPassQrs(ticketOrSale) {
  if (!ticketOrSale || typeof ticketOrSale !== "object") return [];

  const passList = Array.isArray(ticketOrSale.passQrcodes)
    ? ticketOrSale.passQrcodes
    : Array.isArray(ticketOrSale.passQrCodes)
      ? ticketOrSale.passQrCodes
      : null;

  if (passList && passList.length > 0) {
    const rows = passList
      .map((p, i) => {
        const qrcode = pickQrStringFromValue(p);
        if (!qrcode) return null;
        const passIndexRaw =
          typeof p === "object" && p != null
            ? p.passIndex ?? p.passindex ?? p.index ?? i + 1
            : i + 1;
        const passIndex = Number(passIndexRaw);
        const ticketId =
          typeof p === "object" && p != null
            ? p.ticketId ?? p.ticketid ?? undefined
            : undefined;
        return {
          qrcode,
          passIndex: Number.isFinite(passIndex) && passIndex > 0 ? passIndex : i + 1,
          ticketId: ticketId != null && ticketId !== "" ? String(ticketId) : undefined,
          key:
            (typeof p === "object" && p != null && (p._id || p.id)) ||
            `${ticketId ?? "pass"}-${i}`,
        };
      })
      .filter(Boolean);
    if (rows.length > 0) {
      rows.sort((a, b) => a.passIndex - b.passIndex);
      return rows;
    }
  }

  const images = Array.isArray(ticketOrSale.qrcodeimages)
    ? ticketOrSale.qrcodeimages
    : Array.isArray(ticketOrSale.qrCodeImages)
      ? ticketOrSale.qrCodeImages
      : Array.isArray(ticketOrSale.qrcodeImages)
        ? ticketOrSale.qrcodeImages
        : null;

  if (images && images.length > 0) {
    const rows = images
      .map((img, i) => {
        const qrcode = pickQrStringFromValue(img);
        if (!qrcode) return null;
        return { qrcode, passIndex: i + 1, key: `img-${i}` };
      })
      .filter(Boolean);
    if (rows.length > 0) return rows;
  }

  const single = String(
    ticketOrSale.qrcode ??
      ticketOrSale.qrCode ??
      ticketOrSale.qrcodeimage ??
      ticketOrSale.qrCodeImage ??
      ""
  ).trim();
  if (!single) return [];
  return [
    {
      qrcode: single,
      passIndex: 1,
      key: ticketOrSale._id ?? ticketOrSale.ticketid ?? ticketOrSale.ticketId ?? "single",
    },
  ];
}

function unitKey(row, i) {
  return (
    row?._id ??
    row?.orderid ??
    row?.orderId ??
    row?.ticketid ??
    row?.ticketId ??
    i
  );
}

/**
 * @returns {null | {
 *   bundle: boolean,
 *   ticketCount: number,
 *   passCount: number,
 *   rows: Array<{ qrcode: string, key: string|number, raw: object, passes: object[] }>,
 *   customerViewUrl: string,
 * }}
 */
export function parseOfflineSaleSuccessDisplay(res) {
  const data = extractOfflineSaleData(res);
  if (!data || typeof data !== "object") return null;

  const customerViewUrl = String(
    data.viewTicketButton ?? data.viewticketbutton ?? ""
  ).trim();

  if (data.offlineTicketBundle === true) {
    const list = Array.isArray(data.tickets) ? data.tickets : [];
    const rows = list.map((row, i) => {
      const passes = collectTicketPassQrs(row);
      return {
        qrcode: passes[0]?.qrcode ?? "",
        key: unitKey(row, i),
        raw: row,
        passes,
      };
    });
    const tc = Number(data.ticketCount);
    const ticketCount = Number.isFinite(tc) && tc > 0 ? tc : rows.length;
    const passCount = rows.reduce((sum, r) => sum + r.passes.length, 0);
    return { bundle: true, ticketCount, passCount, rows, customerViewUrl };
  }

  const passes = collectTicketPassQrs(data);
  const passCountFromApi = Number(data.passCount ?? data.passcount);
  const passCount =
    passes.length > 0
      ? passes.length
      : Number.isFinite(passCountFromApi) && passCountFromApi > 0
        ? passCountFromApi
        : passes.length || 1;

  return {
    bundle: false,
    ticketCount: 1,
    passCount,
    rows: [
      {
        qrcode: passes[0]?.qrcode ?? "",
        key: data._id ?? data.orderid ?? data.orderId ?? "single",
        raw: data,
        passes,
      },
    ],
    customerViewUrl,
  };
}
